import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { FaClock, FaDollarSign, FaCheckCircle, FaSpinner, FaCalendarAlt, FaUserTie, FaArrowLeft, FaMapMarkerAlt, FaTimes, FaCheck, FaListUl, FaFileAlt, FaEdit, FaUser, FaInfoCircle, FaPlus, FaTrash, FaCloudUploadAlt, FaBarcode } from 'react-icons/fa';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { Switch } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import ScanningReportModal from '../components/ScanningReportModal';

// Declaración de tipos para Google Maps
declare global {
  interface Window {
    google: any;
  }
}

interface TaskData {
  id: string;
  title: string;
  description: string;
  columnId: string;
  progress: number;
  startDate: any;
  endDate?: any;
  startTime: string;
  duration: string;
  durationUnit: string;
  finalPrice: number;
  finalPriceUnit: string;
  finalTotal: number;
  dueDate?: string;
  location?: {
    address: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    latitude: number;
    longitude: number;
    zoom?: number;
  };
  checklistEnabled: boolean;
  checklist: any[];
  documents: any[];
  notes: any[];
  projectId: string;
  projectName: string;
  businessId: string;
  businessName: string;
  clientName?: string;
  assignedTo: string;
  createdAt: string;
  updatedAt: string;
  worker?: string | string[];
  isScanningJob?: boolean;
  workerScanningEdit?: string[]; // Array de IDs de workers que pueden editar
  scanningData?: {
    purpose?: string;
    equipment?: string;
    process?: string;
    limitations?: string;
    findings?: string;
    closure?: string;
    checklistItems?: string[];
    images?: Array<{
      id: string;
      scanImage: { url: string; name: string; uploadDate: string };
      diagramImage: { url: string; name: string; uploadDate: string };
    }>;
  };
  scanningReport?: {
    signed?: boolean;
    signedPdf?: {
      url: string;
      signedBy?: { name: string; uid: string };
      signedAt: string;
    };
  };
}

interface UserProfile {
  uid: string;
  displayName?: string;
  photoURL?: string;
  email?: string;
}

// Valores por defecto para Scanning Job Report
const DEFAULT_SCANNING_DATA = {
  purpose: 'The purpose of the project was to locate rebar & utilities within the slab . The client marked out the desired locations prior to the scanning and our markings were then placed onto the concrete using spray paint.',
  equipment: '• Concrete Scanning EM Antenna. This GPR antenna is handheld and rolls over the surface. The antenna needs a reasonably smooth, unobstructed surface for scanning and is not able to scan within 2"-4" of obstructions such as walls and metal tracks unless they are removed prior to our work. The data is displayed on a screen during the scanning and marked on the surface in real time. The total depth achieved can be as much as 18" or more with this antenna but can vary widely depending on the types of materials being scanned through and other factors such as the spacing of the reinforcing and/or moisture content. Depth accuracy depends on the ability to obtain an accurate depth calibration for the concrete. No harmful radiation is emitted and the work can be performed at any time with people in close proximity.',
  process: 'The process begins by using GPR to collect initial scans throughout the area. These scans are used to calibrate the equipment and determine the type of slab, reinforcing patterns, maximum depth penetration, and any other potential limitations. Each location is then scanned in a grid pattern in 4 directions and then rescanned with the antenna cross polarized. The GPR data is viewed in real time and anomalies in the data were located and marked on the surface. Relevant scan examples were saved and will be provided in this report. The EM locator was used to sweep the areas for live power readings and radio frequency signals.',
  limitations: 'Please keep in mind that there are limitations to any subsurface investigation. The equipment may not achieve maximum effectiveness due to conditions in the concrete or soil such as moisture content, age of the concrete, reinforcing size and spacing, and a variety of other factors. Depths are dependent on many factors so depth accuracy can vary throughout a site and should be treated as estimates only. No subsurface investigation or equipment can provide a complete image of what lies below. Our results should always be used in conjunction with as many methods as possible such as consulting existing plans and drawings, visual inspection of above-ground features, drilling or cutting as far as possible from all of our markings, etc.',
  checklistItems: [] as string[],
  images: [] as Array<{
    id: string;
    scanImage: { url: string; name: string; uploadDate: string };
    diagramImage: { url: string; name: string; uploadDate: string };
  }>
};

const WorkerTaskView: React.FC = () => {
  const { taskId, projectId } = useParams<{ taskId: string; projectId: string }>();
  const { currentUser, userData } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [task, setTask] = useState<TaskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingChecklist, setEditingChecklist] = useState(false);
  const [localChecklist, setLocalChecklist] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('details');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [assignedWorkers, setAssignedWorkers] = useState<UserProfile[]>([]);
  const [uploadingScanningImages, setUploadingScanningImages] = useState<{ [key: string]: boolean }>({});
  const [showScanningReportModal, setShowScanningReportModal] = useState(false);
  const [canGenerateReport, setCanGenerateReport] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (taskId && projectId) {
      fetchTaskDetails();
    }
  }, [currentUser, taskId, projectId, navigate]);

  // Sincronizar localChecklist cuando cambia task.checklist
  useEffect(() => {
    if (task && task.checklist) {
      setLocalChecklist(task.checklist);
    }
  }, [task?.checklist]);

  // Verificar permisos de firma
  useEffect(() => {
    const checkSignPermissions = async () => {
      if (!task || !currentUser) return;
      
      try {
        const db = getFirestore();
        // Obtener el proyecto para revisar el staff
        const projectDoc = await getDoc(doc(db, `projects/${task.businessId}/projects`, task.projectId));
        
        if (projectDoc.exists()) {
          const projectData = projectDoc.data();
          const staff = projectData.staff || [];
          
          console.log('[WorkerTaskView] Current user UID:', currentUser.uid);
          console.log('[WorkerTaskView] Project staff:', staff);
          console.log('[WorkerTaskView] Staff count:', staff.length);
          
          // Verificar si algún miembro del staff tiene canSignForMe para el worker actual
          const membersWithPermission = staff.filter((member: any) => {
            const memberId = member.id || member.uid;
            const memberName = `${member.firstName} ${member.lastName}`;
            console.log('[WorkerTaskView] Checking member:', {
              id: memberId,
              name: memberName,
              canSignForMe: member.canSignForMe,
              canSignForMeKeys: member.canSignForMe ? Object.keys(member.canSignForMe) : []
            });
            
            // Si es el mismo worker, no necesita darse permiso a sí mismo
            if (memberId === currentUser.uid) {
              console.log('[WorkerTaskView] Skipping self (worker)');
              return false;
            }
            
            const canSignForMe = member.canSignForMe || {};
            const hasPermission = canSignForMe[currentUser.uid] === true;
            console.log(`[WorkerTaskView] Does ${memberName} allow worker to sign? ${hasPermission}`);
            if (hasPermission) {
              console.log('[WorkerTaskView] ✅ Member with permission:', member);
            }
            return hasPermission;
          });
          
          console.log('[WorkerTaskView] Members who granted permission:', membersWithPermission);
          const hasPermission = membersWithPermission.length > 0;
          
          console.log('[WorkerTaskView] Can generate report:', hasPermission);
          setCanGenerateReport(hasPermission);
        } else {
          console.log('[WorkerTaskView] Project document does not exist');
        }
      } catch (error) {
        console.error('[WorkerTaskView] Error checking sign permissions:', error);
      }
    };
    
    checkSignPermissions();
  }, [task, currentUser]);

  // Cargar datos de los workers asignados
  useEffect(() => {
    const fetchWorkers = async () => {
      if (!task) return;
      const db = getFirestore();
      let workerUids: string[] = [];
      if (Array.isArray(task.worker)) {
        workerUids = task.worker;
      } else if (typeof task.worker === 'string') {
        workerUids = [task.worker];
      } else if (task.assignedTo) {
        workerUids = [task.assignedTo];
      }
      // Elimina duplicados
      workerUids = Array.from(new Set(workerUids));
      if (workerUids.length === 0) return setAssignedWorkers([]);
      const usersSnap = await getDocs(collection(db, 'users'));
      const users: UserProfile[] = [];
      usersSnap.forEach(docSnap => {
        const data = docSnap.data();
        if (workerUids.includes(docSnap.id)) {
          users.push({
            uid: docSnap.id,
            displayName: data.displayName || data.name || data.firstName || '',
            photoURL: data.photoURL || data.profilePhotoURL || '',
            email: data.email || ''
          });
        }
      });
      // Si falta alguno, agrega con solo UID
      workerUids.forEach(uid => {
        if (!users.find(u => u.uid === uid)) {
          users.push({ uid });
        }
      });
      setAssignedWorkers(users);
    };
    fetchWorkers();
  }, [task]);

  const fetchTaskDetails = async () => {
    if (!taskId || !projectId || !currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      const db = getFirestore();
      
      // Buscar el proyecto en todos los negocios
      const businessesSnap = await getDocs(collection(db, 'businesses'));
      let foundTask: TaskData | null = null;
      
      for (const businessDoc of businessesSnap.docs) {
        const businessId = businessDoc.id;
        const businessData = businessDoc.data();
        
        try {
          const projectDoc = await getDoc(doc(db, `projects/${businessId}/projects`, projectId));
          
          if (projectDoc.exists()) {
            const projectData = projectDoc.data() as any;
            
            // Buscar la tarea específica en el array embebido
            let taskData = null;
            if (Array.isArray(projectData.tasks)) {
              taskData = projectData.tasks.find((t: any) => t.id === taskId);
            }
            // Si no está en el array, buscar en la subcolección
            if (!taskData) {
              const taskDoc = await getDoc(doc(db, `projects/${businessId}/projects/${projectId}/tasks`, taskId));
              if (taskDoc.exists()) {
                taskData = { id: taskDoc.id, ...taskDoc.data() };
              }
            }

            if (taskData) {
              // Verificar si el worker está asignado a esta tarea
              const isAssigned = taskData.assignedTo === currentUser.uid || 
                (taskData.worker && (
                  Array.isArray(taskData.worker) ? 
                  taskData.worker.includes(currentUser.uid) : 
                  taskData.worker === currentUser.uid
                ));

              if (isAssigned) {
                // Si es un scanning job pero no tiene scanningData o los campos están vacíos, inicializar con valores por defecto
                const originalScanningData = taskData.scanningData || {};
                let scanningData = originalScanningData;
                let needsUpdate = false;
                
                if (taskData.isScanningJob) {
                  // Verificar qué campos necesitan valores por defecto
                  const hasEmptyFields = !originalScanningData.purpose || 
                                        !originalScanningData.equipment || 
                                        !originalScanningData.process || 
                                        !originalScanningData.limitations;
                  
                  if (hasEmptyFields) {
                    scanningData = {
                      purpose: originalScanningData.purpose || DEFAULT_SCANNING_DATA.purpose,
                      equipment: originalScanningData.equipment || DEFAULT_SCANNING_DATA.equipment,
                      process: originalScanningData.process || DEFAULT_SCANNING_DATA.process,
                      limitations: originalScanningData.limitations || DEFAULT_SCANNING_DATA.limitations,
                      findings: originalScanningData.findings || '',
                      closure: originalScanningData.closure || '',
                      checklistItems: originalScanningData.checklistItems || [],
                      images: originalScanningData.images || []
                    };
                    needsUpdate = true;
                  } else {
                    // Asegurar que todos los campos existan (para compatibilidad)
                    scanningData = {
                      purpose: originalScanningData.purpose || '',
                      equipment: originalScanningData.equipment || '',
                      process: originalScanningData.process || '',
                      limitations: originalScanningData.limitations || '',
                      findings: originalScanningData.findings || '',
                      closure: originalScanningData.closure || '',
                      checklistItems: originalScanningData.checklistItems || [],
                      images: originalScanningData.images || []
                    };
                  }
                }
                
                foundTask = {
                  ...taskData,
                  scanningData: taskData.isScanningJob ? scanningData : taskData.scanningData,
                  projectId,
                  projectName: projectData.name,
                  businessId,
                  businessName: businessData.name || businessData.businessName,
                  clientName: projectData.clientName
                };
                setLocalChecklist(taskData.checklist || []);
                
                // Si necesitamos actualizar con valores por defecto, guardar en Firestore
                if (needsUpdate && taskData.isScanningJob && businessId && projectId && taskId) {
                  const taskDocRef = doc(db, `projects/${businessId}/projects/${projectId}/tasks`, taskId);
                  updateDoc(taskDocRef, { 
                    scanningData: scanningData,
                    updatedAt: new Date().toISOString()
                  }).catch(error => {
                    console.error('Error al guardar valores por defecto de scanning data:', error);
                  });
                }
                
                break;
              }
            }
          }
        } catch (error) {
          // Continuar buscando en otros negocios
          continue;
        }
      }

      if (foundTask) {
        console.log('[WorkerTaskView] Task loaded:', foundTask);
        console.log('[WorkerTaskView] Scanning data:', foundTask.scanningData);
        setTask(foundTask);
      } else {
        setError('Tarea no encontrada o no tienes acceso a esta tarea');
      }
    } catch (error) {
      console.error('Error al obtener detalles de la tarea:', error);
      setError('Error al cargar la tarea');
    } finally {
      setLoading(false);
    }
  };

  // Actualiza el progreso cada vez que se agrega, completa o elimina un check
  const recalculateAndSaveProgress = async (updatedChecklist: any[]) => {
    if (!task || !currentUser) return;
    // Calcular nuevo progreso
    const completedItems = updatedChecklist.filter(item => item.completed).length;
    const newProgress = updatedChecklist.length > 0 ? Math.round((completedItems / updatedChecklist.length) * 100) : 0;
    // Actualizar en Firestore
    const db = getFirestore();
    const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
    await updateDoc(taskDocRef, { checklist: updatedChecklist, progress: newProgress });
    setTask(prev => prev ? { ...prev, checklist: updatedChecklist, progress: newProgress } : null);
  };

  // Modifica updateChecklistItem para usar recalculateAndSaveProgress
  const updateChecklistItem = async (index: number, completed: boolean) => {
    if (!task || !currentUser) return;
    try {
      const updatedChecklist = [...localChecklist];
      updatedChecklist[index] = { ...updatedChecklist[index], completed };
      setLocalChecklist(updatedChecklist);
      await recalculateAndSaveProgress(updatedChecklist);
    } catch (error) {
      console.error('Error al actualizar checklist:', error);
    }
  };

  // Modifica addChecklistItem para usar recalculateAndSaveProgress
  const addChecklistItem = async () => {
    if (!task || !currentUser || !newChecklistItem.trim()) {
      return;
    }
    try {
      setAddingItem(true);
      const newItem = {
        id: Date.now().toString(),
        text: newChecklistItem.trim(),
        completed: false,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      };
      const updatedChecklist = [...localChecklist, newItem];
      setLocalChecklist(updatedChecklist);
      setNewChecklistItem('');
      await recalculateAndSaveProgress(updatedChecklist);
    } catch (error: any) {
      console.error('💥 Error al añadir item al checklist:', error);
    } finally {
      setAddingItem(false);
    }
  };

  // Modifica removeChecklistItem para usar recalculateAndSaveProgress
  const removeChecklistItem = async (index: number) => {
    if (!task || !currentUser) return;
    try {
      const updatedChecklist = localChecklist.filter((_, i) => i !== index);
      setLocalChecklist(updatedChecklist);
      await recalculateAndSaveProgress(updatedChecklist);
    } catch (error) {
      console.error('Error al eliminar item del checklist:', error);
    }
  };

  const getTaskStatusColor = (columnId: string) => {
    switch (columnId) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'done': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaskStatusText = (columnId: string) => {
    switch (columnId) {
      case 'new': return t('workerTaskView.status.new');
      case 'in_progress': return t('workerTaskView.status.inProgress');
      case 'done': return t('workerTaskView.status.done');
      default: return columnId;
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString(i18n.language);
    }
    return new Date(date).toLocaleDateString(i18n.language);
  };

  const formatDateTime = (date: any, time?: string) => {
    if (!date) return '-';
    const dateStr = formatDate(date);
    return time ? `${dateStr} ${time}` : dateStr;
  };

  const tabs = [
    { id: 'details', label: t('workerTaskView.tabs.details'), icon: FaInfoCircle },
    { id: 'location', label: t('workerTaskView.tabs.location'), icon: FaMapMarkerAlt },
    { id: 'assignment', label: t('workerTaskView.tabs.assignment'), icon: FaUser },
    { id: 'files', label: t('workerTaskView.tabs.files'), icon: FaFileAlt },
    { id: 'checklist', label: t('workerTaskView.tabs.checklist'), icon: FaListUl },
    ...(task?.isScanningJob ? [{ id: 'scanning', label: t('projects.kanban.scanningJob.report'), icon: FaBarcode }] : []),
  ];

  // Componente del mapa
  const TaskMap = ({ location }: { location: any }) => {
    const [mapLoaded, setMapLoaded] = useState(false);

    // Funciones para abrir en apps de navegación
    const openInGoogleMaps = () => {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`;
      window.open(url, '_blank');
    };

    const openInAppleMaps = () => {
      const url = `https://maps.apple.com/?q=${encodeURIComponent(location.address)}`;
      window.open(url, '_blank');
    };

    const openInWaze = () => {
      const url = `https://waze.com/ul?q=${encodeURIComponent(location.address)}&navigate=yes`;
      window.open(url, '_blank');
    };

    // Detectar si es iOS para mostrar Apple Maps
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    useEffect(() => {
      // Cargar Google Maps API
      const loadGoogleMaps = () => {
        if (window.google && window.google.maps) {
          setMapLoaded(true);
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyB41DRUbKWJHPxaFjMAwdrzWzbVKartNGg&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => setMapLoaded(true);
        document.head.appendChild(script);
      };

      loadGoogleMaps();
    }, []);

    useEffect(() => {
      if (!mapLoaded || !location) return;

      const map = new window.google.maps.Map(document.getElementById('task-map') as HTMLElement, {
        center: { lat: location.latitude, lng: location.longitude },
        zoom: location.zoom || 15,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      // Agregar marcador
      new window.google.maps.Marker({
        position: { lat: location.latitude, lng: location.longitude },
        map: map,
        title: location.address,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 0C10.477 0 6 4.477 6 10c0 7 10 22 10 22s10-15 10-22c0-5.523-4.477-10-10-10zm0 16c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z" fill="#DC2626"/>
              <circle cx="16" cy="10" r="4" fill="#FFFFFF"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 32)
        }
      });

      // Agregar InfoWindow
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; max-width: 200px;">
            <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${location.address}</h3>
            <p style="margin: 0; font-size: 12px; color: #666;">
              ${location.city}, ${location.state} ${location.zipCode}<br>
              ${location.country}
            </p>
          </div>
        `
      });

      // Mostrar InfoWindow al hacer clic en el marcador
      map.addListener('click', () => {
        infoWindow.open(map, new window.google.maps.Marker({
          position: { lat: location.latitude, lng: location.longitude },
          map: map
        }));
      });

    }, [mapLoaded, location]);

    if (!location) return null;

    return (
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">{t('workerTaskView.location.address')}</h4>
              <p className="text-gray-700">{location.address}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('workerTaskView.location.city')}</h4>
                <p className="text-gray-700">{location.city}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('workerTaskView.location.state')}</h4>
                <p className="text-gray-700">{location.state}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('workerTaskView.location.zipCode')}</h4>
                <p className="text-gray-700">{location.zipCode}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('workerTaskView.location.country')}</h4>
                <p className="text-gray-700">{location.country}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de navegación */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FaMapMarkerAlt className="text-red-500" />
            {t('workerTaskView.location.navigation')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              onClick={openInGoogleMaps}
              className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
            >
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FLogos%20ext%2FRecurso%201.svg?alt=media&token=af164c2b-3f0a-4932-9390-bfb27e40a056"
                alt="Google Maps"
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-medium text-gray-700">{t('workerTaskView.location.googleMaps')}</span>
            </button>
            
            {isIOS && (
              <button
                onClick={openInAppleMaps}
                className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
              >
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">A</span>
                </div>
                <span className="font-medium text-gray-700">{t('workerTaskView.location.appleMaps')}</span>
              </button>
            )}
            
            <button
              onClick={openInWaze}
              className="flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
            >
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FLogos%20ext%2Fwaze-app-icon-seeklogo.svg?alt=media&token=334eac66-b721-4be5-8288-07997d60a6b4"
                alt="Waze"
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-medium text-gray-700">{t('workerTaskView.location.waze')}</span>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3 text-center">
            {t('workerTaskView.location.navigationHint')}
          </p>
        </div>

        {/* Mapa */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FaMapMarkerAlt className="text-red-500" />
            {t('workerTaskView.location.map')}
          </h4>
          <div 
            id="task-map" 
            className="w-full h-80 rounded-lg border border-gray-200"
            style={{ minHeight: '320px' }}
          >
            {!mapLoaded && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FaSpinner className="animate-spin text-2xl text-red-600 mx-auto mb-2" />
                  <p className="text-gray-600 text-sm">{t('workerTaskView.location.loadingMap')}</p>
                </div>
              </div>
            )}
          </div>
          <div className="mt-3 text-xs text-gray-500 text-center">
            {t('workerTaskView.location.mapInstructions')}
          </div>
        </div>
      </div>
    );
  };

  // Función para subir archivo
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!task || !currentUser) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const storage = getStorage();
      const filePath = `tasks/${task.businessId}/${task.projectId}/${task.id}/${Date.now()}_${file.name}`;
      const fileRef = storageRef(storage, filePath);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      // Actualizar documentos en Firestore
      const db = getFirestore();
      const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
      const taskDocSnap = await getDoc(taskDocRef);
      let documents = [];
      if (taskDocSnap.exists()) {
        const data = taskDocSnap.data();
        documents = Array.isArray(data.documents) ? data.documents : [];
      }
      const newDoc = { name: file.name, url, uploadedAt: new Date().toISOString(), uploadedBy: currentUser.uid, visible: true };
      const updatedDocs = [...documents, newDoc];
      await updateDoc(taskDocRef, { documents: updatedDocs });
      setTask(prev => prev ? { ...prev, documents: updatedDocs } : null);
    } catch (error: any) {
      setUploadError(error.message || 'Error uploading file');
    } finally {
      setUploading(false);
    }
  };

  // Función para eliminar archivo
  const handleDeleteFile = async (docIndex: number) => {
    if (!task || !isBusinessOwner) return;
    const db = getFirestore();
    const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
    const taskDocSnap = await getDoc(taskDocRef);
    let documents = [];
    if (taskDocSnap.exists()) {
      const data = taskDocSnap.data();
      documents = Array.isArray(data.documents) ? data.documents : [];
    }
    const fileToDelete = documents[docIndex];
    documents.splice(docIndex, 1);
    await updateDoc(taskDocRef, { documents });
    setTask(prev => prev ? { ...prev, documents } : null);
    // Eliminar de Storage
    if (fileToDelete && fileToDelete.url) {
      try {
        const storage = getStorage();
        const fileRef = storageRef(storage, fileToDelete.url);
        await deleteObject(fileRef);
      } catch (e) { /* ignorar error de storage */ }
    }
  };
  // Función para cambiar visibilidad
  const handleToggleVisibility = async (docIndex: number) => {
    if (!task || !isBusinessOwner) return;
    const db = getFirestore();
    const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
    const taskDocSnap = await getDoc(taskDocRef);
    let documents = [];
    if (taskDocSnap.exists()) {
      const data = taskDocSnap.data();
      documents = Array.isArray(data.documents) ? data.documents : [];
    }
    documents[docIndex].visible = !documents[docIndex].visible;
    await updateDoc(taskDocRef, { documents });
    setTask(prev => prev ? { ...prev, documents } : null);
  };

  // Funciones para Scanning Job Report
  const handleAddScanningImageSet = async () => {
    if (!task) return;
    const imageSetId = `scan-${Date.now()}-${Math.random()}`;
    
    const newImageSet = {
      id: imageSetId,
      scanImage: { url: '', name: '', uploadDate: '' },
      diagramImage: { url: '', name: '', uploadDate: '' }
    };
    
    const updatedTask = {
      ...task,
      scanningData: {
        ...task.scanningData,
        images: [
          ...(task.scanningData?.images || []),
          newImageSet
        ]
      }
    };
    
    setTask(updatedTask);
    
    // Guardar en Firestore
    try {
      const db = getFirestore();
      const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
      await updateDoc(taskDocRef, { scanningData: updatedTask.scanningData });
      toast.success('Image set added');
    } catch (error) {
      console.error('Error adding image set:', error);
      toast.error('Error adding image set');
    }
  };

  const handleScanningImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    imageSetId: string,
    imageType: 'scanImage' | 'diagramImage'
  ) => {
    console.log('[WorkerTaskView] handleScanningImageUpload called', { imageSetId, imageType });
    
    const files = event.target.files;
    console.log('[WorkerTaskView] Files:', files);
    
    if (!files || files.length === 0) {
      console.log('[WorkerTaskView] No files selected');
      return;
    }
    
    if (!task?.businessId) {
      console.log('[WorkerTaskView] No businessId from task');
      toast.error('Missing business ID');
      return;
    }
    
    if (!task?.projectId) {
      console.log('[WorkerTaskView] No projectId');
      toast.error('Missing project ID');
      return;
    }
    
    if (!task?.id) {
      console.log('[WorkerTaskView] No task id');
      toast.error('Missing task ID');
      return;
    }

    const fileKey = `${imageSetId}-${imageType}`;
    setUploadingScanningImages((prev) => ({ ...prev, [fileKey]: true }));

    try {
      const file = files[0];
      console.log('[WorkerTaskView] Uploading file:', file.name);
      
      const fileExtension = file.name.split('.').pop();
      const fileName = `${imageSetId}-${imageType}.${fileExtension}`;
      
      const storage = getStorage();
      const storageReference = ref(storage, `projects/${task.businessId}/tasks/${task.id}/scanning/${fileName}`);
      
      console.log('[WorkerTaskView] Storage path:', `projects/${task.businessId}/tasks/${task.id}/scanning/${fileName}`);
      
      await uploadBytes(storageReference, file);
      const url = await getDownloadURL(storageReference);
      
      console.log('[WorkerTaskView] File uploaded, URL:', url);

      // Actualizar el estado local y Firestore
      const updatedTask = {
        ...task,
        scanningData: {
          ...task.scanningData,
          images: (task.scanningData?.images || []).map((img: any) =>
            img.id === imageSetId
              ? {
                  ...img,
                  [imageType]: {
                    url,
                    name: file.name,
                    uploadDate: new Date().toISOString()
                  }
                }
              : img
          )
        }
      };
      setTask(updatedTask);

      // Guardar en Firestore
      const db = getFirestore();
      const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
      await updateDoc(taskDocRef, { scanningData: updatedTask.scanningData });
      
      console.log('[WorkerTaskView] Firestore updated successfully');

      toast.success(t('projects.kanban.fileUploaded'));
    } catch (error) {
      console.error('[WorkerTaskView] Error uploading scanning image:', error);
      toast.error(t('projects.kanban.uploadError'));
    } finally {
      setUploadingScanningImages((prev) => {
        const newState = { ...prev };
        delete newState[fileKey];
        return newState;
      });
      // Reset input
      event.target.value = '';
    }
  };

  const handleScanningChecklistChange = async (itemText: string, checked: boolean) => {
    if (!task) return;
    
    const currentItems = task.scanningData?.checklistItems || [];
    let updatedItems: string[];
    
    if (checked) {
      updatedItems = [...currentItems, itemText];
    } else {
      updatedItems = currentItems.filter((item: string) => item !== itemText);
    }
    
    const updatedTask = {
      ...task,
      scanningData: {
        ...task.scanningData,
        checklistItems: updatedItems
      }
    };
    setTask(updatedTask);
    
    // Guardar en Firestore
    try {
      const db = getFirestore();
      const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
      await updateDoc(taskDocRef, { scanningData: updatedTask.scanningData });
    } catch (error) {
      console.error('Error updating checklist:', error);
    }
  };

  const handleRemoveScanningImageSet = async (imageSetId: string) => {
    if (!task) return;
    
    const updatedTask = {
      ...task,
      scanningData: {
        ...task.scanningData,
        images: (task.scanningData?.images || []).filter((img: any) => img.id !== imageSetId)
      }
    };
    
    setTask(updatedTask);
    
    // Guardar en Firestore
    try {
      const db = getFirestore();
      const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
      await updateDoc(taskDocRef, { scanningData: updatedTask.scanningData });
      toast.success('Image set removed');
    } catch (error) {
      console.error('Error removing image set:', error);
      toast.error('Error removing image set');
    }
  };

  const handleScanningDataChange = (field: string, value: string) => {
    if (!task) return;
    
    const updatedTask = {
      ...task,
      scanningData: {
        ...task.scanningData,
        [field]: value
      }
    };
    setTask(updatedTask);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-red-600 mx-auto mb-4" />
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
            <FaTimes className="text-6xl text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Error</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button
              onClick={() => navigate('/worker-dashboard')}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-200"
            >
              Volver al dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  const isBusinessOwner = userData && task && userData.businessId === task.businessId;
  const visibleDocuments = isBusinessOwner ? (task.documents || []) : (task.documents || []).filter((doc: any) => doc.visible !== false);

  // Permitir edición de checklist a cualquier usuario asignado a la tarea
  const canEditChecklist = !!task && currentUser && (task.assignedTo === currentUser.uid || (task.worker && Array.isArray(task.worker) ? task.worker.includes(currentUser.uid) : task.worker === currentUser?.uid));

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/worker-projects/${task.projectId}`)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-3 rounded-xl transition-colors duration-200"
              >
                <FaArrowLeft className="text-xl" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{task.title}</h1>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTaskStatusColor(task.columnId)}`}>
                    {getTaskStatusText(task.columnId)}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaUserTie className="text-red-500" />
                    {task.businessName}
                  </span>
                  <span className="flex items-center gap-1">
                    <HiOutlineDocumentText className="text-red-500" />
                    {task.projectName}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Información de la tarea */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">{t('workerTaskView.progress')}</p>
                  <p className="text-3xl font-bold">{task.progress}%</p>
                </div>
                <FaCheckCircle className="text-4xl opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">{t('workerTaskView.payment')}</p>
                  <p className="text-3xl font-bold">${task.finalTotal?.toLocaleString() || '0'}</p>
                </div>
                <FaDollarSign className="text-4xl opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">{t('workerTaskView.duration')}</p>
                  <p className="text-3xl font-bold">
                    {task.duration} {task.durationUnit === 'days' ? t('workerTaskView.days') : t('workerTaskView.hours')}
                  </p>
                </div>
                <FaClock className="text-4xl opacity-80" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex space-x-8 px-8 min-w-max" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-red-500 text-red-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="text-lg" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workerTaskView.details.title')}</h3>
                  {task.description && (
                    <div className="bg-gray-50 rounded-xl p-6 mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">{t('workerTaskView.details.description')}</h4>
                      <p className="text-gray-700">{task.description}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <FaCalendarAlt className="text-red-500" />
                        {t('workerTaskView.details.schedule')}
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('workerTaskView.startDate')}:</span>
                          <span className="font-medium">{formatDateTime(task.startDate, task.startTime)}</span>
                        </div>
                        {task.endDate && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('workerTaskView.endDate')}:</span>
                            <span className="font-medium">{formatDate(task.endDate)}</span>
                          </div>
                        )}
                        {task.dueDate && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('workerTaskView.details.dueDate')}:</span>
                            <span className="font-medium">{formatDate(task.dueDate)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('workerTaskView.duration')}:</span>
                          <span className="font-medium">{task.duration} {task.durationUnit === 'days' ? t('workerTaskView.days') : t('workerTaskView.hours')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <FaDollarSign className="text-red-500" />
                        {t('workerTaskView.details.payment')}
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('workerTaskView.details.total')}:</span>
                          <span className="font-medium text-green-600">${task.finalTotal?.toLocaleString() || '0'}</span>
                        </div>
                        {task.finalPrice && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('workerTaskView.details.rate')}:</span>
                            <span className="font-medium">${task.finalPrice} {task.finalPriceUnit === 'hour' ? t('workerTaskView.perHour') : t('workerTaskView.perDay')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Location Tab */}
            {activeTab === 'location' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workerTaskView.location.title')}</h3>
                {task.location ? (
                  <TaskMap location={task.location} />
                ) : (
                  <div className="text-center py-8">
                    <FaMapMarkerAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('workerTaskView.location.noLocation')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Assignment Tab */}
            {activeTab === 'assignment' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workerTaskView.assignment.title')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {assignedWorkers.length === 0 ? (
                    <div className="col-span-full text-gray-500 text-center">No hay trabajadores asignados a esta tarea.</div>
                  ) : (
                    assignedWorkers.map(worker => (
                      <div key={worker.uid} className="bg-white rounded-xl shadow-md p-6 flex items-center gap-4 border border-gray-100">
                        <img
                          src={worker.photoURL || '/public/assets/react.svg'}
                          alt={worker.displayName || worker.uid}
                          className="w-14 h-14 rounded-full object-cover border border-gray-200"
                          onError={e => (e.currentTarget.src = '/public/assets/react.svg')}
                        />
                        <div>
                          <div className="font-semibold text-gray-900 text-lg">{worker.displayName || worker.uid}</div>
                          <div className="text-gray-500 text-sm">{worker.email || ''}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Files Tab */}
            {activeTab === 'files' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workerTaskView.files.title')}</h3>
                <div className="mb-6">
                  <div
                    className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer transition-all duration-200 ${uploading ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-red-400 hover:bg-red-50'}`}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                    style={{ minHeight: 120 }}
                  >
                    <FaCloudUploadAlt className={`text-4xl mb-2 ${uploading ? 'text-blue-400' : 'text-red-400'}`} />
                    <span className="font-medium text-gray-700 mb-1">{uploading ? 'Subiendo archivo...' : 'Haz click o arrastra un archivo aquí para subirlo'}</span>
                    <span className="text-xs text-gray-400">PDF, imágenes, Word, Excel, etc. (máx 10MB)</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </div>
                  {uploadError && <div className="mt-2 text-red-600 text-sm">{uploadError}</div>}
                </div>
                {visibleDocuments.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {visibleDocuments.map((doc: any, index: number) => (
                      <div key={index} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm flex items-center gap-4 relative">
                        <FaFileAlt className="text-red-500 text-3xl flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 text-base mb-1">
                            {doc.name || `${t('workerTaskView.files.document')} ${index + 1}`}
                          </h4>
                          <div className="text-xs text-gray-500 mb-1">
                            {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString(i18n.language) : ''}
                          </div>
                          {doc.url && (
                            <a 
                              href={doc.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-block mt-1 text-blue-600 hover:text-blue-800 text-sm font-medium border border-blue-100 rounded px-3 py-1 transition-colors duration-200"
                            >
                              {t('workerTaskView.files.viewDocument')}
                            </a>
                          )}
                        </div>
                        {isBusinessOwner && (
                          <div className="flex flex-col items-end gap-2 ml-2">
                            <Switch
                              checked={doc.visible !== false}
                              onChange={() => handleToggleVisibility(index)}
                              className={`${doc.visible !== false ? 'bg-green-500' : 'bg-gray-300'} relative inline-flex h-6 w-11 items-center rounded-full`}
                            >
                              <span className="sr-only">Visible</span>
                              <span
                                className={`${doc.visible !== false ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                              />
                            </Switch>
                            <button
                              onClick={() => handleDeleteFile(index)}
                              className="text-red-500 hover:text-red-700 text-xs mt-1"
                              title="Eliminar archivo"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('workerTaskView.files.noFiles')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Checklist Tab */}
            {activeTab === 'checklist' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{t('workerTaskView.checklist.title')}</h3>
                  {canEditChecklist && (
                    <button
                      onClick={() => setEditingChecklist(!editingChecklist)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl font-semibold transition-colors duration-200 flex items-center gap-2"
                    >
                      <FaEdit className="text-sm" />
                      {editingChecklist ? t('common.save') : t('common.edit')}
                    </button>
                  )}
                </div>
                {/* Formulario para añadir nuevos items */}
                {canEditChecklist && editingChecklist && (
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder={t('workerTaskView.checklist.itemPlaceholder')}
                      value={newChecklistItem}
                      onChange={e => setNewChecklistItem(e.target.value)}
                      disabled={addingItem}
                    />
                    <button
                      onClick={addChecklistItem}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold text-sm"
                      disabled={addingItem || !newChecklistItem.trim()}
                    >
                      <FaPlus className="inline mr-1" /> {t('common.add')}
                    </button>
                  </div>
                )}
                {(task.checklistEnabled || (localChecklist && localChecklist.length > 0)) && localChecklist && localChecklist.length > 0 ? (
                  <div className="space-y-4">
                    {localChecklist.map((item, index) => {
                      return (
                        <div key={item.id || index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                          {canEditChecklist ? (
                            <button
                              onClick={() => updateChecklistItem(index, !item.completed)}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                item.completed 
                                  ? 'bg-green-500 border-green-500 hover:bg-green-600' 
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              {item.completed && <FaCheck className="text-white text-xs" />}
                            </button>
                          ) : (
                            <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${item.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>{item.completed && <FaCheck className="text-white text-xs" />}</span>
                          )}
                          <span className={`flex-1 text-lg ${item.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>{item.text}</span>
                          {canEditChecklist && editingChecklist && (
                            <button
                              onClick={() => removeChecklistItem(index)}
                              className="text-red-500 hover:text-red-700 text-xs ml-2"
                              title={t('common.delete')}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      );
                    })}
                    
                    <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-blue-800">{t('workerTaskView.checklistProgress')}</span>
                        <span className="font-semibold text-blue-900">
                          {localChecklist.filter(item => item.completed).length} / {localChecklist.length}
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${localChecklist.length > 0 ? (localChecklist.filter(item => item.completed).length / localChecklist.length) * 100 : 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FaListUl className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('workerTaskView.checklist.noChecklist')}</p>
                    {canEditChecklist && (
                      <p className="text-sm text-gray-400 mt-2">{t('workerTaskView.checklist.startAdding')}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Scanning Tab */}
            {activeTab === 'scanning' && task?.isScanningJob && (() => {
              console.log('[WorkerTaskView] Rendering scanning tab. Task scanningData:', task.scanningData);
              // El worker solo puede editar si workerScanningEdit es true Y el reporte no está firmado
              // El worker puede editar si su ID está en el array workerScanningEdit y el reporte no está firmado
              const canEditScanning = currentUser && 
                Array.isArray(task.workerScanningEdit) && 
                task.workerScanningEdit.includes(currentUser.uid) && 
                !task.scanningReport?.signed;
              return (
              <div className="space-y-6">
                <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded mb-4 text-base">{t('projects.kanban.scanningJob.report')}</span>
                
                {!canEditScanning && !task.scanningReport?.signed && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-yellow-800">
                      <span className="font-medium">ℹ️ Limited editing mode:</span> You can only edit the Findings checklist below. Other fields are read-only. Contact the project administrator if you need to make changes to other sections.
                    </p>
                  </div>
                )}
                
                {/* PURPOSE */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">{t('projects.kanban.scanningJob.purpose')}</label>
                  <textarea
                    value={task.scanningData?.purpose || DEFAULT_SCANNING_DATA.purpose}
                    onChange={e => handleScanningDataChange('purpose', e.target.value)}
                    placeholder="Enter the purpose of the scanning..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={4}
                    disabled={!canEditScanning}
                  />
                </div>

                {/* EQUIPMENT */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">{t('projects.kanban.scanningJob.equipment')}</label>
                  <textarea
                    value={task.scanningData?.equipment || DEFAULT_SCANNING_DATA.equipment}
                    onChange={e => handleScanningDataChange('equipment', e.target.value)}
                    placeholder="Describe the equipment used..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={4}
                    disabled={!canEditScanning}
                  />
                </div>

                {/* PROCESS */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">{t('projects.kanban.scanningJob.process')}</label>
                  <textarea
                    value={task.scanningData?.process || DEFAULT_SCANNING_DATA.process}
                    onChange={e => handleScanningDataChange('process', e.target.value)}
                    placeholder="Describe the scanning process..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={4}
                    disabled={!canEditScanning}
                  />
                </div>

                {/* LIMITATIONS */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">{t('projects.kanban.scanningJob.limitations')}</label>
                  <textarea
                    value={task.scanningData?.limitations || DEFAULT_SCANNING_DATA.limitations}
                    onChange={e => handleScanningDataChange('limitations', e.target.value)}
                    placeholder="Describe the scanning limitations..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={4}
                    disabled={!canEditScanning}
                  />
                </div>

                {/* FINDINGS CHECKLIST */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">{t('projects.kanban.scanningJob.findings')}</label>
                  
                  <div className="space-y-6 bg-gray-50 p-6 rounded-lg border border-gray-200">
                    {/* A. Structural Reinforcement */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.structuralReinforcement.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                            checked={(task.scanningData?.checklistItems || []).includes("Rebar detected")}
                            onChange={(e) => handleScanningChecklistChange("Rebar detected", e.target.checked)}
                            disabled={task.scanningReport?.signed}
                          />
                          <span>{t('projects.kanban.scanningJob.checklist.structuralReinforcement.rebarDetected')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                            checked={(task.scanningData?.checklistItems || []).includes("Wire mesh detected")}
                            onChange={(e) => handleScanningChecklistChange("Wire mesh detected", e.target.checked)}
                            disabled={task.scanningReport?.signed}
                          />
                          <span>{t('projects.kanban.scanningJob.checklist.structuralReinforcement.wireMeshDetected')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                            checked={(task.scanningData?.checklistItems || []).includes("Post-tension tendons detected")}
                            onChange={(e) => handleScanningChecklistChange("Post-tension tendons detected", e.target.checked)}
                            disabled={task.scanningReport?.signed}
                          />
                          <span>{t('projects.kanban.scanningJob.checklist.structuralReinforcement.postTensionTendons')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                            checked={(task.scanningData?.checklistItems || []).includes("No reinforcement detected in the scanned area")}
                            onChange={(e) => handleScanningChecklistChange("No reinforcement detected in the scanned area", e.target.checked)}
                            disabled={task.scanningReport?.signed}
                          />
                          <span>{t('projects.kanban.scanningJob.checklist.structuralReinforcement.noReinforcement')}</span>
                        </label>
                      </div>
                    </div>

                    {/* B. Detected Utilities */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.detectedUtilities.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Electrical conduit")} onChange={(e) => handleScanningChecklistChange("Electrical conduit", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.electricalConduit')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Communications conduit")} onChange={(e) => handleScanningChecklistChange("Communications conduit", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.communicationsConduit')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Metal pipe")} onChange={(e) => handleScanningChecklistChange("Metal pipe", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.metalPipe')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("PVC pipe")} onChange={(e) => handleScanningChecklistChange("PVC pipe", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.pvcPipe')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Energized utilities detected (EM readings)")} onChange={(e) => handleScanningChecklistChange("Energized utilities detected (EM readings)", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.energizedUtilities')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("No utilities detected")} onChange={(e) => handleScanningChecklistChange("No utilities detected", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.detectedUtilities.noUtilities')}</span>
                        </label>
                      </div>
                    </div>

                    {/* C. Slab Conditions */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.slabConditions.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("High moisture levels in slab")} onChange={(e) => handleScanningChecklistChange("High moisture levels in slab", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.slabConditions.highMoisture')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Interference in signals")} onChange={(e) => handleScanningChecklistChange("Interference in signals", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.slabConditions.interference')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Limited depth penetration")} onChange={(e) => handleScanningChecklistChange("Limited depth penetration", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.slabConditions.limitedDepth')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Irregular surface affected scanning")} onChange={(e) => handleScanningChecklistChange("Irregular surface affected scanning", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.slabConditions.irregularSurface')}</span>
                        </label>
                      </div>
                    </div>

                    {/* D. Surface Markings */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.surfaceMarkings.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Marked in red (electrical)")} onChange={(e) => handleScanningChecklistChange("Marked in red (electrical)", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.surfaceMarkings.markedRed')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Marked in yellow (reinforcement)")} onChange={(e) => handleScanningChecklistChange("Marked in yellow (reinforcement)", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.surfaceMarkings.markedYellow')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("High-risk areas marked")} onChange={(e) => handleScanningChecklistChange("High-risk areas marked", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.surfaceMarkings.highRiskAreas')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Manual exposure required before drilling/cutting")} onChange={(e) => handleScanningChecklistChange("Manual exposure required before drilling/cutting", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.surfaceMarkings.manualExposure')}</span>
                        </label>
                      </div>
                    </div>

                    {/* E. Recommendations */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">{t('projects.kanban.scanningJob.checklist.recommendations.title')}</h4>
                      <div className="space-y-2 ml-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Avoid drilling near marked areas")} onChange={(e) => handleScanningChecklistChange("Avoid drilling near marked areas", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.recommendations.avoidDrilling')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Manually expose marked items before cutting")} onChange={(e) => handleScanningChecklistChange("Manually expose marked items before cutting", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.recommendations.manuallyExpose')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Review structural drawings")} onChange={(e) => handleScanningChecklistChange("Review structural drawings", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.recommendations.reviewDrawings')}</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                          <input type="checkbox" className="w-4 h-4 text-red-600 rounded focus:ring-red-500" checked={(task.scanningData?.checklistItems || []).includes("Recommend re-scan due to interference")} onChange={(e) => handleScanningChecklistChange("Recommend re-scan due to interference", e.target.checked)} disabled={task.scanningReport?.signed} />
                          <span>{t('projects.kanban.scanningJob.checklist.recommendations.recommendRescan')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SCANNING AND DIAGRAM IMAGES */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Scan & Diagram Images</h3>
                    <button
                      type="button"
                      onClick={handleAddScanningImageSet}
                      disabled={!canEditScanning}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      + Add Image Set
                    </button>
                  </div>
                  
                  {(!canEditScanning || task.scanningReport?.signed) && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-yellow-800 mb-2">
                            <span className="font-medium">⚠️ Scanning Report Locked:</span> {task.scanningReport?.signed 
                              ? 'This report has been signed and cannot be edited.' 
                              : 'You do not have permission to edit this scanning report. Contact the project administrator if you need to make changes.'}
                          </p>
                          {task.scanningReport?.signedPdf && (
                            <div className="text-xs text-gray-700 space-y-1 mb-3">
                              <div><span className="font-medium">Signed by:</span> {task.scanningReport.signedPdf.signedBy?.name}</div>
                              <div><span className="font-medium">Date:</span> {new Date(task.scanningReport.signedPdf.signedAt).toLocaleString()}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      {task.scanningReport?.signedPdf?.url && (
                        <div className="flex gap-2">
                          <a
                            href={task.scanningReport.signedPdf.url}
                            download={`scanning-report-${task.id}.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Download Signed PDF
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {(task.scanningData?.images?.length || 0) === 0 ? (
                    <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm">No image sets added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {task.scanningData?.images?.map((imageSet: any, index: any) => (
                        <div key={imageSet.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-gray-700">Image Set {index + 1}</h4>
                            <button
                              type="button"
                              onClick={() => handleRemoveScanningImageSet(imageSet.id)}
                              disabled={!canEditScanning}
                              className="text-red-600 hover:text-red-800 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <FaTimes />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            {/* Scan Image */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Scan Image</label>
                              {imageSet.scanImage.url ? (
                                <div className="space-y-2">
                                  <img 
                                    src={imageSet.scanImage.url} 
                                    alt="Scan" 
                                    className="w-full h-40 object-cover rounded-lg border border-gray-300"
                                  />
                                  <div className="text-xs text-gray-600">
                                    <p className="font-medium truncate">{imageSet.scanImage.name}</p>
                                    <p>{new Date(imageSet.scanImage.uploadDate).toLocaleDateString()}</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTask((prev) => {
                                        if (!prev) return null;
                                        return {
                                          ...prev,
                                          scanningData: {
                                            ...prev.scanningData,
                                            images: (prev.scanningData?.images || []).map((img: any) =>
                                              img.id === imageSet.id
                                                ? {
                                                    ...img,
                                                    scanImage: { url: '', name: '', uploadDate: '' }
                                                  }
                                                : img
                                            )
                                          }
                                        };
                                      });
                                    }}
                                    disabled={!canEditScanning}
                                    className="w-full px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-red-400 transition-colors">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      console.log('[WorkerTaskView] Scan image input onChange triggered');
                                      handleScanningImageUpload(e, imageSet.id, 'scanImage');
                                    }}
                                    disabled={uploadingScanningImages[`${imageSet.id}-scanImage`] || !canEditScanning}
                                    className="hidden"
                                    id={`scan-${imageSet.id}`}
                                  />
                                  <label 
                                    htmlFor={`scan-${imageSet.id}`} 
                                    className={canEditScanning ? "cursor-pointer block" : "cursor-not-allowed block opacity-50"}
                                    onClick={(e) => {
                                      if (!canEditScanning) {
                                        e.preventDefault();
                                        return;
                                      }
                                      console.log('[WorkerTaskView] Label clicked for scan image');
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div className="text-gray-600">
                                      <div className="text-sm mb-1">📸 Click to upload</div>
                                      <div className="text-xs text-gray-500">or drag and drop</div>
                                    </div>
                                  </label>
                                  {uploadingScanningImages[`${imageSet.id}-scanImage`] && (
                                    <div className="mt-2 flex justify-center">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Diagram Image */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Diagram Image</label>
                              {imageSet.diagramImage.url ? (
                                <div className="space-y-2">
                                  <img 
                                    src={imageSet.diagramImage.url} 
                                    alt="Diagram" 
                                    className="w-full h-40 object-cover rounded-lg border border-gray-300"
                                  />
                                  <div className="text-xs text-gray-600">
                                    <p className="font-medium truncate">{imageSet.diagramImage.name}</p>
                                    <p>{new Date(imageSet.diagramImage.uploadDate).toLocaleDateString()}</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTask((prev) => {
                                        if (!prev) return null;
                                        return {
                                          ...prev,
                                          scanningData: {
                                            ...prev.scanningData,
                                            images: (prev.scanningData?.images || []).map((img: any) =>
                                              img.id === imageSet.id
                                                ? {
                                                    ...img,
                                                    diagramImage: { url: '', name: '', uploadDate: '' }
                                                  }
                                                : img
                                            )
                                          }
                                        };
                                      });
                                    }}
                                    disabled={!canEditScanning}
                                    className="w-full px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-red-400 transition-colors">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      console.log('[WorkerTaskView] Diagram image input onChange triggered');
                                      handleScanningImageUpload(e, imageSet.id, 'diagramImage');
                                    }}
                                    disabled={uploadingScanningImages[`${imageSet.id}-diagramImage`] || !canEditScanning}
                                    className="hidden"
                                    id={`diagram-${imageSet.id}`}
                                  />
                                  <label 
                                    htmlFor={`diagram-${imageSet.id}`} 
                                    className={canEditScanning ? "cursor-pointer block" : "cursor-not-allowed block opacity-50"}
                                    onClick={(e) => {
                                      if (!canEditScanning) {
                                        e.preventDefault();
                                        return;
                                      }
                                      console.log('[WorkerTaskView] Label clicked for diagram image');
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div className="text-gray-600">
                                      <div className="text-sm mb-1">📊 Click to upload</div>
                                      <div className="text-xs text-gray-500">or drag and drop</div>
                                    </div>
                                  </label>
                                  {uploadingScanningImages[`${imageSet.id}-diagramImage`] && (
                                    <div className="mt-2 flex justify-center">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* CLOSURE */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">{t('projects.kanban.scanningJob.closure')}</label>
                  <textarea
                    value={task.scanningData?.closure || ''}
                    onChange={e => handleScanningDataChange('closure', e.target.value)}
                    placeholder="Enter closing remarks or summary..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={4}
                    disabled={!canEditScanning}
                  />
                  {!canEditScanning && (
                    <p className="mt-1 text-xs text-yellow-600">
                      ⚠️ {task.scanningReport?.signed 
                        ? 'This field is locked because the report has been signed. Delete the signed PDF to make changes.' 
                        : 'You do not have permission to edit this field. Contact the project administrator if you need to make changes.'}
                    </p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate(`/worker-projects/${task.projectId}`)}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm sm:text-base font-medium transition-colors duration-200 hover:bg-gray-50"
                  >
                    {t('common.cancel')}
                  </button>
                  {canGenerateReport && (
                    <button
                      type="button"
                      onClick={() => setShowScanningReportModal(true)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm sm:text-base font-medium transition-colors duration-200"
                    >
                      {t('projects.kanban.scanningJob.generateReport')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      if (!task) return;
                      try {
                        const db = getFirestore();
                        const taskDocRef = doc(db, `projects/${task.businessId}/projects/${task.projectId}/tasks`, task.id);
                        await updateDoc(taskDocRef, { 
                          scanningData: task.scanningData,
                          updatedAt: new Date().toISOString()
                        });
                        toast.success(t('common.changesSaved') || 'Changes saved successfully');
                      } catch (error) {
                        console.error('Error saving changes:', error);
                        toast.error(t('common.errorSaving') || 'Error saving changes');
                      }
                    }}
                    disabled={task.scanningReport?.signed}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm sm:text-base font-medium transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Scanning Report Modal */}
      {showScanningReportModal && (
        <ScanningReportModal
          isOpen={showScanningReportModal}
          onClose={() => setShowScanningReportModal(false)}
          onPdfSigned={async () => {
            // Recargar la tarea para reflejar el estado actualizado
            if (task?.id && task?.businessId && task?.projectId) {
              try {
                const db = getFirestore();
                const taskRef = doc(db, 'projects', task.businessId, 'projects', task.projectId, 'tasks', task.id);
                const taskSnap = await getDoc(taskRef);
                if (taskSnap.exists()) {
                  const updatedTask = { ...task, ...taskSnap.data() };
                  setTask(updatedTask);
                  console.log('[WorkerTaskView] Task recargado con estado actualizado');
                }
              } catch (error) {
                console.error('[WorkerTaskView] Error recargando task:', error);
              }
            }
          }}
          task={task}
          currentUser={currentUser}
          userData={userData}
        />
      )}
    </div>
  );
};

export default WorkerTaskView; 